import { PageResult } from '@/models/page/page-result';
import { useCallback, useState } from 'react';
import {
  ApiConfig,
  DynamicUrl,
  PageState,
  Run,
  RunWith,
  RunWithPayload,
  RunWithUrlArg,
  RunWithUrlArgAndPayload,
  StaticUrl,
} from '../constant';
import { cancel, request } from '../http';

const initialState: PageState<any> = {
  loading: false,
  total: 0,
  page: 1,
  per_page: 20,
  data: [],
  error: null,
};

type RequestPageState<T extends any[], P, R extends RunWith<T, P>> = PageState<T> & {
  cancel: () => void;
  run: R;
};

function useRequestPage<T extends any[] = []>(config: ApiConfig<StaticUrl>): RequestPageState<T, void, Run<T>>;
function useRequestPage<T extends any[] = [], P = undefined>(
  config: ApiConfig<StaticUrl>,
): RequestPageState<T, P, RunWithPayload<T, P>>;
function useRequestPage<T extends any[] = []>(
  config: ApiConfig<DynamicUrl>,
): RequestPageState<T, void, RunWithUrlArg<T>>;
function useRequestPage<T extends any[] = [], P = undefined>(
  config: ApiConfig<DynamicUrl>,
): RequestPageState<T, P, RunWithUrlArgAndPayload<T, P>>;
function useRequestPage<T extends any[] = [], P = undefined>(config: ApiConfig<any>) {
  const [state, setState] = useState<PageState<T>>({ ...initialState });

  const wrappedRun = useCallback(
    async (arg_0?: any, arg_1?: any, arg_2?: any) => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const result = await request<PageResult<T, string>, P>(config, arg_0, arg_1, arg_2);
        if (!result) {
          setState({ ...initialState });
          return [];
        }
        setState((prev) => ({
          ...prev,
          loading: false,
          data: result._embedded[config.embeddedField || 'data'],
          total: result.total,
          page: result.page,
          per_page: result.per_page,
        }));
        return result._embedded[config.embeddedField || 'data'] || [];
      } catch (error: any) {
        setState({ ...initialState, error });
        throw error;
      }
    },
    [config],
  );

  const wrappedCancel = useCallback(() => {
    cancel(config);
  }, [config]);

  return {
    ...state,
    data: state.data || [],
    cancel: wrappedCancel,
    run: wrappedRun,
  };
}

export default useRequestPage;
